//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import { apis } from 'utils/lib/apis';

//  -- Actions ------------------------
import * as Actions from "./actions";
import * as NotificationActions from "../notification/actions";
import * as OnegameAction from "../one_game/actions";
import { logToSentry } from "utils/networkErrorUtils";
import { isArray, has, isEmpty, get } from "lodash";
import { handleListClassQuizzesForStudent } from "store/classQuizzes/thunks";
import { handleListClassActivitiesForStudent } from "store/classActivities/thunks";
import { handleListCustomAssignment } from "store/activities/thunks";
import { updateClassActivitiesFromSchoologySocket } from "store/classActivities/actions";
import { updateClassQuizzesFromSchoologySocket } from "store/classQuizzes/actions";
import { handleListClassSubmissions } from "store/submissions/thunks";
import * as SubmissionsActions from '../submissions/actions';
import { handleUpdateSrtIntr } from "store/interactions/thunks";
import { handleUpdateVideoSrt } from "store/resources/thunks";
// import { handleAddOrUpdateOneWorksheet } from "store/worksheet/thunks";
import { updateWorksheetName } from "store/worksheet/actions";
import { handleAddOrUpdateArticleLevel, handleAddOrUpdateOneStory, handleListStoryAiConfig } from "store/StoryAiCofig/thunks";
import { handleListBookUnits } from "store/booksUnits/thunks";
import { handleListBooksByIds } from "store/books/thunks";
import * as FeedbackActions from 'store/feedback/actions';
import * as PermissionsActions from 'store/permissions/actions';
//  ----------------------------------------------------------------------------
//  Thunks
//  ----------------------------------------------------------------------------

let aiGradingDebounceTimer = null;
let canvasGradeSyncDebounceTimer = null;
let systemNotificationDebounceTimer = null;
let bookUnitNotificationDebounceTimer = null;
let chatMetaRefreshDebounceTimer = null;

//  -- Create log ----------------
const collectInboxPreviewMessages = (chatUsers) => {
  if (!isArray(chatUsers)) {
    return [];
  }
  const byId = new Map();
  for (const bucket of chatUsers) {
    const bLast = get(bucket, "chatMeta.lastBroadcastMessage");
    if (bLast && bLast.id) {
      byId.set(bLast.id, bLast);
    }
    for (const u of bucket.user || []) {
      const lm = u.lastChatMessage;
      if (lm && lm.id) {
        byId.set(lm.id, lm);
      }
    }
  }
  return Array.from(byId.values());
};

export const handleGetChatUsers = (userId, options = {}) => async (dispatch) => {
  try {
    const reqOpts = options.chatMeta
      ? { queryParams: { chatMeta: 1 } }
      : undefined;
    const chatUsers = await apis.get("chat", `/users/${userId}`, reqOpts);
    await dispatch(Actions.setChatUser(chatUsers));
    if (options.chatMeta && isArray(chatUsers)) {
      const previews = collectInboxPreviewMessages(chatUsers);
      if (previews.length > 0) {
        dispatch(Actions.mergeMessages(previews));
      }
    }
    return chatUsers;
  } catch (error) {
    logToSentry({
      message: 'Error getting chat users',
      error,
      module: 'CHAT',
      operation: 'GET_CHAT_USERS',
      tags: { userId }
    });
    return error;
  }
};

const scheduleChatUsersMetaRefresh = (dispatch, userId) => {
  if (!userId) {
    return;
  }
  if (chatMetaRefreshDebounceTimer) {
    clearTimeout(chatMetaRefreshDebounceTimer);
  }
  chatMetaRefreshDebounceTimer = setTimeout(() => {
    dispatch(handleGetChatUsers(userId, { chatMeta: true }));
    chatMetaRefreshDebounceTimer = null;
  }, 450);
};

export const handleMessageChat = (message, userId, currentUser, currentClass) => async (dispatch) => {
  try {
    let data = {};
    if (typeof message == "object") {
      data = message;
    } else {
        try {
            data = JSON.parse(message);
        } catch (error) {
            logToSentry({
                message: 'Failed to parse chat socket message',
                error,
                module: 'CHAT',
                extra: {
                    payloadPreview: (message || '').slice(0, 200),
                    payloadLength: message?.length || 0,
                },
            });
            return;
        }
    }
    if (data.code == "connect") {
      await dispatch(Actions.setUserActiveStatus(data.userId));
    } else if (data.code == "disconnect") {
      await dispatch(Actions.setUserDeactiveStatus(data.userId));
    } else if (data.code == "message") {
      await dispatch(handleUpdateOnCreateMessageChat(data));
      scheduleChatUsersMetaRefresh(dispatch, userId);
    } else if (
      data.code === "seen_deliver_update" ||
      data.code === "seen_deliver_update_brodcast"
    ) {
      if (isArray(data.data) && data.data.length > 0) {
        await dispatch(Actions.updateSeenDelivery(data.data));
        scheduleChatUsersMetaRefresh(dispatch, userId);
      }
    } else if (data.code === "sent") {
      if (typeof data.data == "object" && has(data.data, "id")) {
        await dispatch(Actions.updateSeenDelivery([data.data]));
        scheduleChatUsersMetaRefresh(dispatch, userId);
      }
    } else if (data.code === "user_config_updated") {
      if (!isEmpty(data.config) && data.config.id) {
        await dispatch(Actions.updateUsersConfigStatus(data.config));
      }
    } else if (data.code === "seen_deliver_dispatched") {
      if (has(data, "with")) {
        await dispatch(
          Actions.updateUserSeenMessageByUserId(get(data, "with"))
        );
        scheduleChatUsersMetaRefresh(dispatch, userId);
      }
    } else if (data.code === "system_notification_dispatched") {
      await dispatch(
        NotificationActions.updateNotificationSuccess(get(data, "data", ""))
      );
    } else if (data.code === "feedback_notification") {
      if (data.type === "teacher_report_issue" && data.receiverId === userId) {
        await dispatch(FeedbackActions.invokeFeedbackWithTeacherDescription(
          data.teacherDescription,
          data.teacherName,
          data.audioUrl,
          data.email || null
        ));
      }
    } else if (data.code === "feedback_notification_dispatched") {
      await dispatch(FeedbackActions.setFeedbackReportResult({ success: true }));
    } else if (data.code === "feedback_notification_failed") {
      await dispatch(FeedbackActions.setFeedbackReportResult({ success: false, reason: data.reason || "receiver_offline" }));
    } else if (data.code === "system_notification") {
      const ACTION_TYPES = ['create_activity', 'create_quiz', 'update_quiz', 'update_activity', 'delete_activity', 'delete_quiz']
      if (ACTION_TYPES.includes(data.type) && currentUser && currentUser.role == "student" && currentClass?.id) {
        if (systemNotificationDebounceTimer) {
          clearTimeout(systemNotificationDebounceTimer);
        }
        systemNotificationDebounceTimer = setTimeout(async () => {
          if (currentClass?.id && currentUser?.id) {
            await Promise.all([
              dispatch(handleListClassQuizzesForStudent(currentClass.id, currentUser.id)),
              dispatch(handleListClassActivitiesForStudent(currentClass.id, currentUser.id)),
              dispatch(handleListCustomAssignment(get(currentUser, "role"), get(currentUser, "id")))
            ])
          }
          systemNotificationDebounceTimer = null;
        }, 1000);
      }
      await dispatch(NotificationActions.updateNotificationSuccess(data));
    } else if (data.code === "play_game_notification") {
      await dispatch(OnegameAction.setPlayGameModal({ open: true, data }));
    } 
    else if (data.code === "stop_game_notification" && data?.userId === currentClass?.teacherId) {
      await dispatch(OnegameAction.setPlayGameModal({ open: false, data:{} }));
    }
    else if (data.code === "permissions_updated") {
      if (data.userId === userId) {
        const permissionsData = {
          permissions:data.permissions,
          user_id: userId
        };
        await dispatch(PermissionsActions.updateUserPermissionsViaSocket(permissionsData));
      }
    } 
    else if (data.code === "ai_grading_notify") {
      if (currentUser.role === "student") {
        if (data.submission && data.submission.id) {
          dispatch(SubmissionsActions.updateSubmissionSuccess(data.submission))
        }
      }else if (currentUser.role === "teacher") {
        dispatch(SubmissionsActions.updateSubmissionSuccess(data.teacher_submission))
      }

    }else if (data.code == 'srt_update_interation_sp' || data.code == 'srt_update_interation_en') {
      if (data && data.data && data.data.id) {
        dispatch(handleUpdateSrtIntr(data.data))
      }
    } else if (data.code == 'srt_update_resourse_sp' || data.code == 'srt_update_resourse_en') {
      if (data && data.data && data.data.id) {
        dispatch(handleUpdateVideoSrt(data.data))
      }
    } else if(data.code === 'story_media_completion'){
      dispatch(handleAddOrUpdateOneStory(data.ai_story))
   }
    else if (data.code === 'article_update') {
      dispatch(handleAddOrUpdateArticleLevel(data.data))
   }
   else if(data.code === 'canvas_grade_sync_done'){
      if (currentClass?.id) {
        if (canvasGradeSyncDebounceTimer) {
          clearTimeout(canvasGradeSyncDebounceTimer);
        }
        canvasGradeSyncDebounceTimer = setTimeout(() => {
          if (currentClass?.id) {
            dispatch(handleListClassSubmissions(currentClass.id))
            console.log("on sync done. canvas")
          }
          canvasGradeSyncDebounceTimer = null;
        }, 1000);
      }
   }
    else if (data.code === 'schoology_assignment_created') {
      const assignments = get(data, 'data.assignments', []);
      if (currentClass?.id && assignments.length > 0) {
        const payload = { assignments, currentClassId: currentClass.id };
        dispatch(updateClassActivitiesFromSchoologySocket(payload));
        dispatch(updateClassQuizzesFromSchoologySocket(payload));
      }
      await dispatch(NotificationActions.updateNotificationSuccess(data));
    }
    else if (data.code === 'worksheet_media_completion') {
      dispatch(updateWorksheetName(data.data))
    }
    else if (data.code === 'soy_historias_unshare') {
      if (currentUser && currentUser.id && data.chapter) {
        const is_student = currentUser.role === "student";
        const user_id = currentUser.id;
        const class_id = currentClass?.id;
        dispatch(handleListStoryAiConfig({ chapter: data.chapter, user_id, class_id }, is_student));
      }
    }
    else if (data.notification_action_code === 'admin_book_unit_action' && data.code) {
      const BOOK_UNIT_ACTIONS = ['book_unit_add', 'book_unit_update', 'book_unit_delete'];
      if (BOOK_UNIT_ACTIONS.includes(data.code)) {
        const bookId = get(data, 'bookId');
        if (bookId) {
          if (bookUnitNotificationDebounceTimer) {
            clearTimeout(bookUnitNotificationDebounceTimer);
          }
          bookUnitNotificationDebounceTimer = setTimeout(async () => {
            await Promise.all([
              dispatch(handleListBooksByIds([bookId])),
              dispatch(handleListBookUnits())
            ]);
            bookUnitNotificationDebounceTimer = null;
          }, 1000);
        }
      }
    }
  } catch (error) {
    console.log(error, "rrr");
    if (error?.message !== "Network Error") {
      logToSentry({
          message: 'Error handling message chat',
          error,
          module: 'CHAT',
          operation: 'HANDLE_MESSAGE'
      });
    }
  }
};

export const handleSendMessageChat = (message) => async (dispatch) => {
  try {
    dispatch(Actions.createMessage(message));
    // const messageCreated = await apis.post("chat", "/createMessage", {
    // 	body: message
    // })
    // if(get(messageCreated,"id")){
    // 	dispatch(Actions.setMessageStored(get(messageCreated,"id")))
    // }
  } catch (error) {
    logToSentry({
      message: 'Error sending message chat',
      error,
      module: 'CHAT',
      operation: 'SEND_MESSAGE'
    });
    console.log(error, "erere");
  }
};
export const handleUpdateOnCreateMessageChat =
  (message) => async (dispatch) => {
    dispatch(Actions.createMessage(message));
    dispatch(Actions.removeScheduledMessage(message.id));
  };
export const handleUpdateSeenMessageByUserId = (userId) => async (dispatch) => {
  dispatch(Actions.updateUserSeenMessageByUserId(userId));
};
export const handleGetMessagesChat = (userId) => async (dispatch) => {
  try {
    const allMessages = await apis.get("chat", "/getMessage/" + userId);
    dispatch(Actions.setMessage(allMessages));
    return allMessages;
  } catch (error) {
    if (error && error.message != "Network Error") {
      logToSentry({
          message: 'Error getting messages chat',
          error,
          module: 'CHAT',
          operation: 'GET_MESSAGES'
      });
    }
    return null;
  }
};

export const handleGetMessageThread =
  ({ userId, classId, peerId, broadcast, limit = 40, before = null }) =>
  async (dispatch) => {
    try {
      const queryParams = { classId, limit };
      if (broadcast) {
        queryParams.broadcast = 1;
      } else {
        queryParams.withUserId = peerId;
      }
      if (before) {
        queryParams.before =
          typeof before === "string" ? before : new Date(before).toISOString();
      }
      const data = await apis.get("chat", `/messageThread/${userId}`, {
        queryParams,
      });
      if (data && data.error) {
        logToSentry({
          message: "Error getting message thread",
          error: new Error(String(data.error)),
          module: "CHAT",
          operation: "GET_MESSAGE_THREAD",
          tags: { userId, classId },
        });
        return { messages: [], hasMore: false };
      }
      const messages = isArray(data?.messages)
        ? data.messages
        : isArray(data)
          ? data
          : [];
      if (messages.length > 0) {
        dispatch(Actions.mergeMessages(messages));
      }
      return { messages, hasMore: Boolean(data?.hasMore) };
    } catch (error) {
      if (error && error.message != "Network Error") {
        logToSentry({
          message: "Error getting message thread",
          error,
          module: "CHAT",
          operation: "GET_MESSAGE_THREAD",
          tags: { userId, classId },
        });
      }
      return { messages: [], hasMore: false };
    }
  };
export const handleGetUserChatConfig = (userId) => async (dispatch) => {
  try {
    const config = await apis.get("chat", "/getUserConfig/" + userId);
    dispatch(Actions.updateChatUserConfig(config));
    return config;
  } catch (error) {
    logToSentry({
      message: 'Error getting user chat config',
      error,
      module: 'CHAT',
      operation: 'GET_USER_CONFIG',
      tags: { userId }
    });
  }
};

export const handleUpdateUserChatAvatar =
  (userId, avatar) => async (dispatch) => {
    try {
      const config = await apis.put("chat", "/updateAvatar", {
        body: {
          id: userId,
          avatar: avatar,
        },
      });
      dispatch(Actions.updateChatUserConfig(config));
      return config;
    } catch (error) {
      if (error && error.message != "Network Error") {
        logToSentry({
            message: 'Error updating user chat avatar',
            error,
            module: 'CHAT',
            operation: 'UPDATE_AVATAR',
            tags: { userId }
        });
      }
    }
  };
export const handleUpdateUserChatHour =
  (userId, chatHour) => async (dispatch) => {
    try {
      const config = await apis.put("chat", "/updateChatHour", {
        body: {
          id: userId,
          chatHour: chatHour,
        },
      });
      dispatch(Actions.updateChatUserConfig(config));
      return config;
    } catch (error) {
      if (error && error.message != "Network Error") {
        logToSentry({
            message: 'Error updating user chat hour',
            error,
            module: 'CHAT',
            operation: 'UPDATE_CHAT_HOUR',
            tags: { userId }
        });
      }
    }
  };
export const handleUpdateUserChatSeen =
  (userId, messageSeen) => async (dispatch) => {
    try {
      const config = await apis.put("chat", "/updateMessageSeenConfig", {
        body: {
          id: userId,
          messageSeen: messageSeen,
        },
      });
      dispatch(Actions.updateChatUserConfig(config));
      return config;
    } catch (error) {
      if (error && error.message != "Network Error") {
        logToSentry({
            message: 'Error updating user chat seen',
            error,
            module: 'CHAT',
            operation: 'UPDATE_CHAT_SEEN',
            tags: { userId }
        });
      }
    }
  };
export const handleUpdateUserChatDiable =
  (id, disabled) => async (dispatch) => {
    try {
      const config = await apis.put("chat", "/enableDisbaleChat", {
        body: { id, disabled },
      });
      dispatch(Actions.updateChatUserConfig(config));
      return config;
    } catch (error) {
      if (error && error.message != "Network Error") {
        logToSentry({
            message: 'Error updating user chat disable',
            error,
            module: 'CHAT',
            operation: 'UPDATE_CHAT_DISABLE',
            tags: { id }
        });
      }
    }
  };
export const handleUpdateUNotificationConfig =
  (userId, notification) => async (dispatch) => {
    try {
      const config = await apis.put("chat", "/updateNotificationConfig", {
        body: {
          id: userId,
          notification: notification,
        },
      });
      dispatch(Actions.updateChatUserConfig(config));
      return config;
    } catch (error) {
      if (error && error.message != "Network Error") {
        logToSentry({
            message: 'Error updating user notification config',
            error,
            module: 'CHAT',
            operation: 'UPDATE_NOTIFICATION',
            tags: { userId }
        });
      }
    }
  };

//scheduled message
export const handleCreateScheduleMessage = (data) => async (dispatch) => {
  try {
    const messageCreated = await apis.post("chat", "/createScheduledMessage", {
      body: data,
    });
    await dispatch(Actions.createScheduledMessage(messageCreated));
    return messageCreated;
  } catch (error) {
    if (error && error.message != "Network Error") {
      logToSentry({
          message: 'Error creating scheduled message',
          error,
          module: 'CHAT',
          operation: 'CREATE_SCHEDULED_MESSAGE'
      });
    }
    console.log(error, "erere");
  }
};
export const handleGetScheduleMessage = (userId) => async (dispatch) => {
  try {
    const scheduledMessages = await apis.get(
      "chat",
      `/getScheduledMessage/${userId}`
    );
    await dispatch(Actions.setScheduledMessage(scheduledMessages));
    return scheduledMessages;
  } catch (error) {
    if (error && error.message != "Network Error") {
      logToSentry({
          message: 'Error getting scheduled message',
          error,
          module: 'CHAT',
          operation: 'GET_SCHEDULED_MESSAGE',
          tags: { userId }
      });
    }
    console.log(error, "erere");
  }
};
export const handleRemoveScheduleMessage = (id) => async (dispatch) => {
  try {
    await apis.del("chat", `/deleteScheduledMessage/${id}`);
    await dispatch(Actions.removeScheduledMessage(id));
  } catch (error) {
    if (error && error.message != "Network Error") {
      logToSentry({
          message: 'Error removing scheduled message',
          error,
          module: 'CHAT',
          operation: 'REMOVE_SCHEDULED_MESSAGE',
          tags: { id }
      });
    }
    console.log(error, "erere");
  }
};
export const handleUpdateScheduleMessage = (data) => async (dispatch) => {
  try {
    const messageCreated = await apis.put("chat", "/updateScheduledMessage", {
      body: data,
    });
    await dispatch(Actions.updateScheduledMessage(messageCreated));
    return messageCreated;
  } catch (error) {
    if (error && error.message != "Network Error") {
      logToSentry({
          message: 'Error updating scheduled message',
          error,
          module: 'CHAT',
          operation: 'UPDATE_SCHEDULED_MESSAGE'
      });
    }
    console.log(error, "erere");
  }
};

export const handleListChatInfo = (currentUser) => async (dispatch) => {
  if (
    get(currentUser, "role") == "teacher" ||
    get(currentUser, "role") == "student"
  ) {
    const userId = get(currentUser, "id");
    dispatch(handleGetChatUsers(userId, { chatMeta: true }));
    dispatch(handleGetUserChatConfig(userId));
  }
};
